import { inject, Injectable, signal } from '@angular/core'
import { map, Observable, of, switchMap, tap } from 'rxjs'
import { MemberService } from '@features/insured/services/member/member.service'
import { MemberApiResponse } from '@features/insured/models/member/member.model'
import { PeriodService } from '@features/insured/services/period/period.service'
import { PolicyService } from '@features/insured/services/policy/policy.service'
import { PolicyApiResponse } from '@features/insured/models/policy/policy.model'
import { PeriodApiResponse } from '@features/insured/models/period/period.model'
import { CoverageService } from '@features/insured/services/coverages/coverage.service'
import { CoverageApiResponse } from '@features/insured/models/coverages/coverage-api.model'
import { CoveragePersistenceService } from '@features/insured/services/coverages/coverage-persistence.service'
import { AviationHullApiResponse, AviationLiabilities, CommercialGeneralLiability } from '@features/insured/models/coverages/base-coverage.model'
import { hasActualValue } from '@core/utils/form-utils'

/**
 * Forms snapshot used by the save pipeline for coverages.
 * When a form is undefined it is skipped (e.g. the user only edits one of the two).
 */
export interface CoverageFormSnapshots {
  aviationHull?: AviationHullApiResponse
  aviationLiabilities?: AviationLiabilities
  commercialGeneralLiability?: CommercialGeneralLiability
}

export interface SaveInsuredRequest {
  member: MemberApiResponse
  policy?: PolicyApiResponse
  coverages?: CoverageFormSnapshots
}

export interface SaveInsuredResult {
  member: MemberApiResponse
  policy?: PolicyApiResponse
  coverages?: CoverageApiResponse[]
}

@Injectable({
  providedIn: 'root'
})
export class InsuredDataFacade {
  public readonly isCreateMode = signal(false)
  public readonly periodOptions = signal<{ label: string; value: string }[]>([])
  public readonly policyOptions = signal<{ label: string; value: string }[]>([])
  public readonly policies = signal<PolicyApiResponse[]>([])

  private readonly memberService = inject(MemberService)
  private readonly periodService = inject(PeriodService)
  private readonly policyService = inject(PolicyService)
  private readonly coverageService = inject(CoverageService)
  private readonly coveragePersistence = inject(CoveragePersistenceService)

  /**
   * Loads all data for the insured detail view.
   */
  public getInsured(uuid: string): Observable<MemberApiResponse> {
    return this.memberService.getById(uuid).pipe(
      tap(() => this.isCreateMode.set(false))
    )
  }

  /**
   * Saves the member and (optionally) the policy and its coverages/subcoverages.
   *
   * Pipeline (each step is conditional on its input being present):
   *   1. Member (POST or PUT)
   *   2. Policy (linked to the saved member's uuid)
   *   3. Coverages + their subcoverages (linked to the saved policy's uuid)
   *   4. Policy reload, because the backend derives its coverage type from the subcoverages saved in step 3
   */
  public saveInsured(request: SaveInsuredRequest): Observable<SaveInsuredResult> {
    return this.memberService.save(request.member).pipe(
      switchMap(savedMember =>
        this.savePolicyIfNeeded(savedMember, request.policy).pipe(
          switchMap(policyResult => this.saveCoveragesIfNeeded(policyResult.policy, request.coverages).pipe(
            switchMap(coverages => this.reloadPolicyIfCoveragesSaved(policyResult.policy, coverages).pipe(
              map(policy => this.buildResult(savedMember, policy, coverages))
            ))
          ))
        )
      )
    )
  }

  public savePolicy(policyData: PolicyApiResponse): Observable<PolicyApiResponse> {
    return this.policyService.save(policyData)
  }

  public loadPeriods(): Observable<PeriodApiResponse[]> {
    return this.periodService.listAll().pipe(
      tap(periods => {
        const options = periods.map(p => ({
          label: p.label,
          value: p.uuid!
        }))
        this.periodOptions.set(options)
      })
    )
  }

  public loadPolicies(memberUuid: string, periodUuid: string): Observable<PolicyApiResponse[]> {
    return this.policyService.getByMemberIdAndPeriodId(memberUuid, periodUuid).pipe(
      tap(policies => {
        this.policies.set(policies)
        const options = policies.map(p => ({
          label: p.label,
          value: p.uuid!
        }))
        this.policyOptions.set(options)
      })
    )
  }

  public loadCoverages(policyId: string): Observable<CoverageApiResponse[]> {
    return this.coverageService.getByPolicyId(policyId)
  }

  private buildResult(
    member: MemberApiResponse,
    policy: PolicyApiResponse | undefined,
    coverages: CoverageApiResponse[] | undefined
  ): SaveInsuredResult {
    const result: SaveInsuredResult = { member }
    if (policy) result.policy = policy
    if (coverages) result.coverages = coverages
    return result
  }

  private savePolicyIfNeeded(
    savedMember: MemberApiResponse,
    policyData?: PolicyApiResponse
  ): Observable<{ policy?: PolicyApiResponse }> {
    if (!policyData || !hasActualValue(policyData.periodId)) {
      return of({})
    }
    const payload: PolicyApiResponse = { ...policyData, memberId: savedMember.uuid! }
    return this.policyService.save(payload).pipe(map(policy => ({ policy })))
  }

  private reloadPolicyIfCoveragesSaved(
    policy: PolicyApiResponse | undefined,
    coverages: CoverageApiResponse[] | undefined
  ): Observable<PolicyApiResponse | undefined> {
    if (!policy || !hasActualValue(policy.uuid) || !coverages) {
      return of(policy)
    }
    return this.policyService.getById(policy.uuid)
  }

  private saveCoveragesIfNeeded(
    policy: PolicyApiResponse | undefined,
    coverageForms?: CoverageFormSnapshots
  ): Observable<CoverageApiResponse[] | undefined> {
    if (!policy || !hasActualValue(policy.uuid) || !coverageForms) {
      return of(undefined)
    }
    return this.coveragePersistence.persistCoveragesForPolicy(
      policy.uuid,
      coverageForms.aviationHull ?? null,
      coverageForms.aviationLiabilities ?? null,
      coverageForms.commercialGeneralLiability ?? null
    )
  }
}
